// ./src/components/features/Contacts/ContactEditDrawer/ContactEditDrawer.tsx
//
// Unified create/edit drawer for a Contact (person). Self-loading in edit mode
// via recordId; context-or-props resolution; no success toast (the page
// highlight is the confirmation). Fields are grouped into sections (name,
// contact, work, address, birthday, group, tags, notes, status).

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Text,
  Divider,
  Checkbox,
  Group,
  Skeleton,
  Switch,
  ActionIcon,
  Tooltip,
  Collapse,
  Anchor,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { Person, PersonInput } from "@/utils/api";
import { getFormErrors, formatErrors, DRAWER_COMBOBOX_PROPS } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS, AddressData } from "@/hooks";
import { useAppEnvironment, useDefaultPhoneCountry } from "@/contexts";
import { useOptionalPersonsContext } from "@/contexts/PersonsContext";
import {
  AppDrawer,
  StatusInput,
  TagsSelectorInput,
  AddressAutocompleteInput,
  BirthdayInput,
  PhoneField,
  AppIcon,
  MediaImageField,
  AutocompleteField,
  SelectField,
} from "@/components/generic";
import type { MediaImageRef } from "@/components/generic";
import { PersonGroupSelect } from "../PersonGroupSelect/PersonGroupSelect";

// Common pronoun suggestions (free text still allowed).
const PRONOUN_OPTIONS = ["she/her", "he/him", "they/them", "she/they", "he/they", "ze/zir", "prefer not to say"];

// Common relationship suggestions (free text still allowed).
const RELATIONSHIP_OPTIONS = [
  "Spouse", "Partner", "Parent", "Mother", "Father", "Child", "Son", "Daughter",
  "Sibling", "Brother", "Sister", "Grandparent", "Grandchild", "Guardian",
  "Dependent", "Friend", "Colleague", "Other",
];

// Role within a household/company group (mirrors PERSON_GROUP_ROLE on the API).
const GROUP_ROLE_VALUES = ["primary", "adult", "minor", "dependent"] as const;

// Sensitive (special-category) — mirror the API's PERSON_SEX / PERSON_GENDER.
const SEX_VALUES = ["male", "female", "intersex", "unspecified"] as const;
const GENDER_VALUES = ["male", "female", "non_binary", "other", "prefer_not_to_say"] as const;

// A bare domain ("acme.com") → a real URL so the open-in-new-tab action works.
const normalizeWebsiteUrl = (url: string): string => {
  const v = url.trim();

  if (!v) {
    return v;
  }

  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

// A small trailing action inside email/phone/website inputs — mail, call, or open.
const ChannelAction: React.FC<{ kind: "email" | "phone" | "website"; value: string; label: string }> = ({
  kind,
  value,
  label,
}) => {
  const v = value.trim();

  if (!v) {
    return null;
  }

  const href = kind === "email" ? `mailto:${v}` : kind === "phone" ? `tel:${v}` : normalizeWebsiteUrl(v);
  const icon = kind === "email" ? "Mail" : kind === "phone" ? "Phone" : "ExternalLink";

  return (
    <Tooltip label={label} withArrow>
      <ActionIcon
        component="a"
        href={href}
        target={kind === "website" ? "_blank" : undefined}
        rel="noopener noreferrer"
        variant="subtle"
        color="gray"
        aria-label={label}
      >
        <AppIcon iconName={icon} size={16} />
      </ActionIcon>
    </Tooltip>
  );
};

// ============================================
// Types
// ============================================

export interface ContactEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  onLoad?: (id: string) => Promise<OpResult>;
  onCreate?: (data: PersonInput) => Promise<OpResult>;
  onUpdate?: (id: string, data: PersonInput) => Promise<OpResult>;
  onCreated?: (person: Person) => void;
  onUpdated?: (person: Person) => void;
  isCreating?: boolean;
  isUpdating?: boolean;
  lastError?: OpResult | null;
  onClearError?: () => void;
}

interface FormData {
  /** The contact's photo (#69) — shown for them on every event that has no photo of its own for the guest. */
  photo: MediaImageRef | null;
  nameTitle: string;
  firstName: string;
  middleName: string;
  lastName: string;
  nameSuffix: string;
  pronouns: string;
  email: string;
  phone: string;
  personalWebsite: string;
  company: string;
  jobTitle: string;
  companyDepartment: string;
  companyWebsite: string;
  country: string;
  stateProvince: string;
  city: string;
  postalCode: string;
  streetAddress: string;
  extraAddress: string;
  birthday: string; // "YYYY-MM-DD"
  birthdayIncludesYear: boolean;
  personGroupId: string | null;
  personGroupRole: string;
  familyRelationship: string;
  bio: string;
  dietaryRestrictions: string;
  sex: string;
  gender: string;
  genderSelfDescribed: string;
  sensitiveConsent: boolean;
  tagIds: string[];
  notes: string;
  status: number;
}

// MUST match the server's OpResult error field names — and ONLY names this form
// actually renders. `nameTitle` / `nameSuffix` are submitted but have no input
// here, so listing them would park their refusals in `fields` where nothing shows
// them; left off, they fall through to the footer prefixed with their name.
const KNOWN_FORM_FIELDS = [
  "firstName", "middleName", "lastName", "pronouns",
  "email", "phone", "personalWebsite",
  "company", "jobTitle", "companyDepartment", "companyWebsite",
  "country", "stateProvince", "city", "postalCode", "streetAddress", "extraAddress",
  "birthdayYear", "birthdayMonth", "birthdayDay",
  "personGroupId", "personGroupRole", "familyRelationship",
  "bio", "dietaryRestrictions", "sex", "gender", "genderSelfDescribed", "sensitiveDataConsentAt",
  "tagIds", "notes", "status", "avatarUploadedFileId",
];

// A stable ISO timestamp for "consent recorded now".
const nowIso = (): string => new Date().toISOString();

const getInitialFormData = (): FormData => ({
  nameTitle: "",
  firstName: "",
  middleName: "",
  lastName: "",
  nameSuffix: "",
  pronouns: "",
  photo: null,
  email: "",
  phone: "",
  personalWebsite: "",
  company: "",
  jobTitle: "",
  companyDepartment: "",
  companyWebsite: "",
  country: "",
  stateProvince: "",
  city: "",
  postalCode: "",
  streetAddress: "",
  extraAddress: "",
  birthday: "",
  birthdayIncludesYear: true,
  personGroupId: null,
  personGroupRole: "",
  familyRelationship: "",
  bio: "",
  dietaryRestrictions: "",
  sex: "",
  gender: "",
  genderSelfDescribed: "",
  sensitiveConsent: false,
  tagIds: [],
  notes: "",
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: Person): FormData => ({
  nameTitle: record.nameTitle || "",
  firstName: record.firstName || "",
  middleName: record.middleName || "",
  lastName: record.lastName || "",
  nameSuffix: record.nameSuffix || "",
  pronouns: record.pronouns || "",
  // ⓘ Keep the id even when the url is missing, so a save never drops a photo it could not preview.
  photo: record.avatarUploadedFileId
    ? { uploadedFileId: record.avatarUploadedFileId, url: record.avatarUploadedFile?.fileUrl ?? record.avatarUploadedFile?.thumbnailUrl ?? null }
    : null,
  email: record.email || "",
  phone: record.phone || "",
  personalWebsite: record.personalWebsite || "",
  company: record.company || "",
  jobTitle: record.jobTitle || "",
  companyDepartment: record.companyDepartment || "",
  companyWebsite: record.companyWebsite || "",
  country: record.country || "",
  stateProvince: record.stateProvince || "",
  city: record.city || "",
  postalCode: record.postalCode || "",
  streetAddress: record.streetAddress || "",
  extraAddress: record.extraAddress || "",
  birthday: record.birthday || "",
  birthdayIncludesYear: record.birthdayIncludesYear ?? false,
  personGroupId: record.personGroupId || null,
  personGroupRole: record.personGroupRole || "",
  familyRelationship: record.familyRelationship || "",
  bio: record.bio || "",
  dietaryRestrictions: record.dietaryRestrictions || "",
  sex: record.sex || "",
  gender: record.gender || "",
  genderSelfDescribed: record.genderSelfDescribed || "",
  sensitiveConsent: !!record.sensitiveDataConsentAt,
  tagIds: (record.tags ?? []).map((tag) => tag.id!).filter(Boolean),
  notes: record.notes || "",
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

// Birthday parts from the "YYYY-MM-DD" string; year is dropped unless included.
const birthdayParts = (
  birthday: string,
  includesYear: boolean
): { birthdayYear: number | null; birthdayMonth: number | null; birthdayDay: number | null } => {
  if (!birthday) {
    return { birthdayYear: null, birthdayMonth: null, birthdayDay: null };
  }

  const [y, m, d] = birthday.split("-").map((v) => Number(v));

  return {
    birthdayYear: includesYear && Number.isFinite(y) ? y : null,
    birthdayMonth: Number.isFinite(m) ? m : null,
    birthdayDay: Number.isFinite(d) ? d : null,
  };
};

const ContactEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Divider />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={90} radius="sm" />
  </Stack>
);

// ============================================
// Component
// ============================================

export const ContactEditDrawer: React.FC<ContactEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  onLoad,
  onCreate,
  onUpdate,
  onCreated,
  onUpdated,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const { t } = useTranslation(["contacts", "common"]);
  const { isMobile } = useAppEnvironment();

  const personsContext = useOptionalPersonsContext();

  const loadFn = onLoad ?? personsContext?.get;
  const createFn = onCreate ?? personsContext?.create;
  const updateFn = onUpdate ?? personsContext?.update;
  const creating = isCreating || (personsContext?.isCreating ?? false);
  const updating = isUpdating || (personsContext?.isUpdating ?? false);
  const operationError = lastError ?? personsContext?.lastError ?? null;
  const clearOperationError = onClearError ?? personsContext?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<Person | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);
  const [isReadyToLoad, setIsReadyToLoad] = useState(false);

  useEffect(() => {
    if (opened) {
      const frameId = requestAnimationFrame(() => setIsReadyToLoad(true));

      return () => cancelAnimationFrame(frameId);
    }

    setIsReadyToLoad(false);

    return;
  }, [opened]);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  const firstNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!opened || showLoadingSkeleton) {
      return undefined;
    }

    const id = setTimeout(() => firstNameRef.current?.focus(), 120);

    return () => clearTimeout(id);
  }, [opened, showLoadingSkeleton]);

  useEffect(() => {
    let isCancelled = false;

    if (isReadyToLoad && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      const run = async () => {
        const result = await loadFnRef.current!(recordId);

        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Person);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      };

      run();
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [isReadyToLoad, opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);
  const [showExtra, setShowExtra] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  // The submit's own result. CRITICAL: when a caller passes `onCreate`/`onUpdate`
  // (e.g. the guest flow creating a contact via PersonService directly), the call
  // never goes through PersonsContext, so `personsContext.lastError` stays null —
  // relying on it alone silently swallowed every server validation error.
  const [submitError, setSubmitError] = useState<OpResult | null>(null);

  const effectiveError = loadError || submitError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    setSubmitError(null);

    const newFormData = record ? fromRecord(record) : getInitialFormData();

    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode]);

  // ── Field handlers ──
  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleText = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  // Fields on the shared (name, value) contract (e.g. PhoneField, which emits a
  // normalized E.164 value rather than a DOM event).
  const handleField = useCallback((name: string | undefined, value: unknown) => {
    if (!name) return;
    setFormData((prev) => ({ ...prev, [name]: (value ?? "") as string }));
  }, []);

  // Default parse region for the phone field — the brand's space.default_country (fallback US).
  const defaultPhoneCountry = useDefaultPhoneCountry();

  // Address autocomplete: while typing, track the raw text as the street. On a
  // picked/geocoded address, use the STRUCTURED street (full.address1) — not the
  // first arg, which on "use my location" is the full formatted address — and
  // fill the other address fields (the user can still adjust them).
  const handleAddressChange = useCallback((address1: string, full: AddressData | null) => {
    setFormData((prev) => {
      if (!full) {
        return { ...prev, streetAddress: address1 };
      }

      const next = { ...prev, streetAddress: full.address1 ?? address1 };

      if (full.address2) next.extraAddress = full.address2;
      if (full.city) next.city = full.city;
      if (full.region) next.stateProvince = full.region;
      if (full.postalCode) next.postalCode = full.postalCode;
      if (full.country) next.country = full.country;

      return next;
    });
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);
    setSubmitError(null);

    const trimmed = (v: string): string | null => {
      const t2 = v.trim();

      return t2.length > 0 ? t2 : null;
    };

    const { birthdayYear, birthdayMonth, birthdayDay } = birthdayParts(
      formData.birthday,
      formData.birthdayIncludesYear
    );

    const submitData: PersonInput = {
      nameTitle: trimmed(formData.nameTitle),
      firstName: trimmed(formData.firstName),
      middleName: trimmed(formData.middleName),
      lastName: trimmed(formData.lastName),
      nameSuffix: trimmed(formData.nameSuffix),
      pronouns: trimmed(formData.pronouns),
      email: trimmed(formData.email),
      phone: trimmed(formData.phone),
      personalWebsite: trimmed(formData.personalWebsite),
      company: trimmed(formData.company),
      jobTitle: trimmed(formData.jobTitle),
      companyDepartment: trimmed(formData.companyDepartment),
      companyWebsite: trimmed(formData.companyWebsite),
      country: trimmed(formData.country),
      stateProvince: trimmed(formData.stateProvince),
      city: trimmed(formData.city),
      postalCode: trimmed(formData.postalCode),
      streetAddress: trimmed(formData.streetAddress),
      extraAddress: trimmed(formData.extraAddress),
      birthdayYear,
      birthdayMonth,
      birthdayDay,
      personGroupId: formData.personGroupId,
      // Role only applies within a group; clearing the group clears the role.
      personGroupRole: formData.personGroupId ? formData.personGroupRole || null : null,
      familyRelationship: trimmed(formData.familyRelationship),
      bio: trimmed(formData.bio),
      avatarUploadedFileId: formData.photo?.uploadedFileId ?? null,
      dietaryRestrictions: trimmed(formData.dietaryRestrictions),
      // Sensitive (special-category) — consent-gated server-side. We send a
      // consent timestamp when the box is checked (keeping any existing one), else
      // null. gender_self_described only applies when gender = other.
      sex: formData.sex || null,
      gender: formData.gender || null,
      genderSelfDescribed: formData.gender === "other" ? trimmed(formData.genderSelfDescribed) : null,
      sensitiveDataConsentAt: formData.sensitiveConsent ? record?.sensitiveDataConsentAt || nowIso() : null,
      notes: trimmed(formData.notes),
      status: formData.status,
      // Replace the whole tag set with the editor's selection.
      tagIds: formData.tagIds,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (!result) {
      return;
    }

    if (!result.didSucceed()) {
      setSubmitError(result);

      return;
    }

    setIsDirty(false);

    const saved = result.data ? (result.data as Person[])[0] : null;

    if (saved) {
      if (isCreate) {
        onCreated?.(saved);
      } else {
        onUpdated?.(saved);
      }
    }

    onClose();
  }, [formData, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated, record?.sensitiveDataConsentAt]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("contacts:form.editTitle", { defaultValue: "Edit contact" })
    : t("contacts:form.addTitle", { defaultValue: "Add contact" });

  // ============================================
  // Render
  // ============================================

  const sectionLabel = (text: string) => (
    <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="xs">
      {text}
    </Text>
  );

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <ContactEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          {/* Name */}
          <Group grow align="flex-start">
            <TextInput
              ref={firstNameRef}
              label={t("contacts:form.fields.firstName.label", { defaultValue: "First name" })}
              name="firstName"
              value={formData.firstName}
              onChange={handleText}
              error={fieldErrors.firstName}
              size="md"
            />
            <TextInput
              label={t("contacts:form.fields.lastName.label", { defaultValue: "Last name" })}
              name="lastName"
              value={formData.lastName}
              onChange={handleText}
              error={fieldErrors.lastName}
              size="md"
            />
          </Group>
          <Group grow align="flex-start">
            <TextInput
              label={t("contacts:form.fields.middleName.label", { defaultValue: "Middle name" })}
              name="middleName"
              value={formData.middleName}
              onChange={handleText}
              error={fieldErrors.middleName}
              size="md"
            />
            <AutocompleteField
              label={t("contacts:form.fields.pronouns.label", { defaultValue: "Pronouns" })}
              placeholder={t("contacts:form.fields.pronouns.placeholder", { defaultValue: "e.g. they/them" })}
              data={PRONOUN_OPTIONS}
              value={formData.pronouns}
              onChange={(_name, value) => setField("pronouns", value)}
              keepTypedLabel={(text) => t("common:picker.useTyped", { defaultValue: "Use “{{text}}”", text })}
              error={fieldErrors.pronouns}
              comboboxProps={DRAWER_COMBOBOX_PROPS}
              size="md"
            />
          </Group>

          {/* #69 — the contact's photo: shown for them on every event, unless that event has one of its own. */}
          <MediaImageField
            intent="avatar"
            allowUrl={false}
            value={formData.photo}
            onChange={(img) => setField("photo", img)}
            label={t("contacts:form.fields.photo.label", { defaultValue: "Photo" })}
            description={t("contacts:form.fields.photo.description", {
              defaultValue: "Shown for this person wherever an event shows its guests or team. Without one, their Gravatar or initials are used.",
            })}
            addLabel={t("contacts:form.fields.photo.add", { defaultValue: "Add photo" })}
            error={fieldErrors.avatarUploadedFileId}
          />

          <Divider />
          {sectionLabel(t("contacts:form.sections.contact", { defaultValue: "Contact" }))}

          <TextInput
            label={t("contacts:form.fields.email.label", { defaultValue: "Email" })}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleText}
            error={fieldErrors.email}
            size="md"
            rightSection={
              <ChannelAction
                kind="email"
                value={formData.email}
                label={t("contacts:actions.emailPerson", { defaultValue: "Email" })}
              />
            }
            rightSectionPointerEvents="all"
          />
          <Group grow align="flex-start">
            <PhoneField
              label={t("contacts:form.fields.phone.label", { defaultValue: "Phone" })}
              name="phone"
              value={formData.phone}
              onChange={handleField}
              defaultCountry={defaultPhoneCountry}
              error={fieldErrors.phone}
              size="md"
              rightSection={
                <ChannelAction
                  kind="phone"
                  value={formData.phone}
                  label={t("contacts:actions.callPerson", { defaultValue: "Call" })}
                />
              }
              rightSectionPointerEvents="all"
            />
            <TextInput
              label={t("contacts:form.fields.personalWebsite.label", { defaultValue: "Website" })}
              name="personalWebsite"
              value={formData.personalWebsite}
              onChange={handleText}
              error={fieldErrors.personalWebsite}
              size="md"
              rightSection={
                <ChannelAction
                  kind="website"
                  value={formData.personalWebsite}
                  label={t("contacts:actions.openWebsite", { defaultValue: "Open" })}
                />
              }
              rightSectionPointerEvents="all"
            />
          </Group>

          <Divider />
          {sectionLabel(t("contacts:form.sections.work", { defaultValue: "Work" }))}

          <Group grow align="flex-start">
            <TextInput
              label={t("contacts:form.fields.company.label", { defaultValue: "Company" })}
              name="company"
              value={formData.company}
              onChange={handleText}
              error={fieldErrors.company}
              size="md"
            />
            <TextInput
              label={t("contacts:form.fields.jobTitle.label", { defaultValue: "Job title" })}
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleText}
              error={fieldErrors.jobTitle}
              size="md"
            />
          </Group>
          <Group grow align="flex-start">
            <TextInput
              label={t("contacts:form.fields.companyDepartment.label", { defaultValue: "Department" })}
              name="companyDepartment"
              value={formData.companyDepartment}
              onChange={handleText}
              error={fieldErrors.companyDepartment}
              size="md"
            />
            <TextInput
              label={t("contacts:form.fields.companyWebsite.label", { defaultValue: "Company website" })}
              name="companyWebsite"
              value={formData.companyWebsite}
              onChange={handleText}
              error={fieldErrors.companyWebsite}
              size="md"
              rightSection={
                <ChannelAction
                  kind="website"
                  value={formData.companyWebsite}
                  label={t("contacts:actions.openWebsite", { defaultValue: "Open" })}
                />
              }
              rightSectionPointerEvents="all"
            />
          </Group>

          <Divider />
          {sectionLabel(t("contacts:form.sections.address", { defaultValue: "Address" }))}

          {/* Autocomplete fills the city/state/postal/country fields below on
              select; the user can still adjust any of them. */}
          <AddressAutocompleteInput
            label={t("contacts:form.fields.streetAddress.label", { defaultValue: "Street address" })}
            value={formData.streetAddress}
            onChange={handleAddressChange}
            error={fieldErrors.streetAddress}
            withinPortal
            size="md"
          />
          <TextInput
            label={t("contacts:form.fields.extraAddress.label", { defaultValue: "Apt / suite / floor" })}
            name="extraAddress"
            value={formData.extraAddress}
            onChange={handleText}
            error={fieldErrors.extraAddress}
            size="md"
          />
          <Group grow align="flex-start">
            <TextInput
              label={t("contacts:form.fields.city.label", { defaultValue: "City" })}
              name="city"
              value={formData.city}
              onChange={handleText}
              error={fieldErrors.city}
              size="md"
            />
            <TextInput
              label={t("contacts:form.fields.stateProvince.label", { defaultValue: "State / province" })}
              name="stateProvince"
              value={formData.stateProvince}
              onChange={handleText}
              error={fieldErrors.stateProvince}
              size="md"
            />
          </Group>
          <Group grow align="flex-start">
            <TextInput
              label={t("contacts:form.fields.postalCode.label", { defaultValue: "Postal code" })}
              name="postalCode"
              value={formData.postalCode}
              onChange={handleText}
              error={fieldErrors.postalCode}
              size="md"
            />
            <TextInput
              label={t("contacts:form.fields.country.label", { defaultValue: "Country" })}
              name="country"
              value={formData.country}
              onChange={handleText}
              error={fieldErrors.country}
              size="md"
            />
          </Group>

          <Divider />
          {sectionLabel(t("contacts:form.sections.details", { defaultValue: "Details" }))}

          {/* Birthday — full date + age when the year is known, else month/day. */}
          <BirthdayInput
            label={t("contacts:form.fields.birthday.label", { defaultValue: "Birthday" })}
            value={formData.birthday}
            includesYear={formData.birthdayIncludesYear}
            onChange={(value, includesYear) =>
              setFormData((prev) => ({ ...prev, birthday: value, birthdayIncludesYear: includesYear }))
            }
            error={fieldErrors.birthdayMonth || fieldErrors.birthdayDay || fieldErrors.birthdayYear}
            dropdownType={isMobile ? "modal" : "popover"}
            size="md"
          />

          {/* Group (household / company) — with create-in-place. */}
          <PersonGroupSelect
            value={formData.personGroupId}
            onChange={(id) => setField("personGroupId", id)}
            error={fieldErrors.personGroupId}
          />

          {/* Role + relationship — only meaningful within a group. */}
          {formData.personGroupId && (
            <Group grow align="flex-start">
              <SelectField
                label={t("contacts:form.fields.personGroupRole.label", { defaultValue: "Role in group" })}
                placeholder={t("contacts:form.fields.personGroupRole.placeholder", { defaultValue: "Select a role" })}
                data={GROUP_ROLE_VALUES.map((role) => ({
                  value: role,
                  label: t(`contacts:form.groupRole.${role}`, {
                    defaultValue: role.charAt(0).toUpperCase() + role.slice(1),
                  }),
                }))}
                value={formData.personGroupRole || null}
                onChange={(_name, v) => setField("personGroupRole", v || "")}
                error={fieldErrors.personGroupRole}
                clearable
                comboboxProps={DRAWER_COMBOBOX_PROPS}
                size="md"
              />
              <AutocompleteField
                label={t("contacts:form.fields.familyRelationship.label", { defaultValue: "Relationship" })}
                placeholder={t("contacts:form.fields.familyRelationship.placeholder", { defaultValue: "e.g. Spouse, Son" })}
                data={RELATIONSHIP_OPTIONS}
                value={formData.familyRelationship}
                onChange={(_name, value) => setField("familyRelationship", value)}
                keepTypedLabel={(text) => t("common:picker.useTyped", { defaultValue: "Use “{{text}}”", text })}
                error={fieldErrors.familyRelationship}
                comboboxProps={DRAWER_COMBOBOX_PROPS}
                size="md"
              />
            </Group>
          )}

          {/* Tags */}
          <TagsSelectorInput
            label={t("contacts:form.fields.tags.label", { defaultValue: "Tags" })}
            value={formData.tagIds}
            onChange={(_name, tagIds) => setField("tagIds", tagIds)}
            error={fieldErrors.tagIds}
            allowCreate
            maxTags={20}
          />

          {/* Notes (the quick single note) */}
          <Textarea
            label={t("contacts:form.fields.notes.label", { defaultValue: "Notes" })}
            placeholder={t("contacts:form.fields.notes.placeholder", {
              defaultValue: "A quick note about this contact",
            })}
            name="notes"
            value={formData.notes}
            onChange={handleText}
            error={fieldErrors.notes}
            minRows={3}
            autosize
            size="md"
          />

          <Divider />

          {/* Status — disabled contacts stay listed (greyed) and can't be selected. */}
          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />

          {/* Additional details — collapsed by default to keep the form short. */}
          <Divider />
          <Anchor component="button" type="button" size="md" onClick={() => setShowExtra((v) => !v)}>
            <Group gap={4} wrap="nowrap">
              <AppIcon iconName={showExtra ? "ChevronUp" : "ChevronDown"} size={14} />
              {showExtra
                ? t("contacts:form.sections.hideExtra", { defaultValue: "Hide additional details" })
                : t("contacts:form.sections.showExtra", { defaultValue: "Additional details" })}
            </Group>
          </Anchor>

          <Collapse in={showExtra}>
            <Stack gap="md">
              <Textarea
                label={t("contacts:form.fields.bio.label", { defaultValue: "Bio" })}
                name="bio"
                value={formData.bio}
                onChange={handleText}
                error={fieldErrors.bio}
                minRows={2}
                autosize
                size="md"
              />
              <Textarea
                label={t("contacts:form.fields.dietaryRestrictions.label", { defaultValue: "Dietary restrictions" })}
                name="dietaryRestrictions"
                value={formData.dietaryRestrictions}
                onChange={handleText}
                error={fieldErrors.dietaryRestrictions}
                minRows={2}
                autosize
                size="md"
              />

              {/* Sensitive (special-category) — consent-gated server-side. */}
              <Group grow align="flex-start">
                <SelectField
                  label={t("contacts:form.fields.sex.label", { defaultValue: "Sex" })}
                  data={SEX_VALUES.map((v) => ({
                    value: v,
                    label: t(`contacts:form.sex.${v}`, { defaultValue: v.charAt(0).toUpperCase() + v.slice(1) }),
                  }))}
                  value={formData.sex || null}
                  onChange={(_name, v) => setField("sex", v || "")}
                  error={fieldErrors.sex}
                  clearable
                  comboboxProps={DRAWER_COMBOBOX_PROPS}
                  size="md"
                />
                <SelectField
                  label={t("contacts:form.fields.gender.label", { defaultValue: "Gender" })}
                  data={GENDER_VALUES.map((v) => ({
                    value: v,
                    label: t(`contacts:form.gender.${v}`, {
                      defaultValue: v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
                    }),
                  }))}
                  value={formData.gender || null}
                  onChange={(_name, v) => setField("gender", v || "")}
                  error={fieldErrors.gender}
                  clearable
                  comboboxProps={DRAWER_COMBOBOX_PROPS}
                  size="md"
                />
              </Group>
              {formData.gender === "other" && (
                <TextInput
                  label={t("contacts:form.fields.genderSelfDescribed.label", { defaultValue: "Gender (self-described)" })}
                  name="genderSelfDescribed"
                  value={formData.genderSelfDescribed}
                  onChange={handleText}
                  error={fieldErrors.genderSelfDescribed}
                  size="md"
                />
              )}
              {/* The consent checkbox is what writes `sensitiveDataConsentAt`, so a
                  refusal on that key belongs here — and the box is also shown when
                  one arrives, otherwise the message would have nowhere to land. */}
              {(formData.sex || formData.gender || !!fieldErrors.sensitiveDataConsentAt) && (
                <Checkbox
                  label={t("contacts:form.fields.sensitiveConsent.label", {
                    defaultValue: "The contact consented to storing their sex / gender",
                  })}
                  checked={formData.sensitiveConsent}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    setField("sensitiveConsent", checked);
                  }}
                  error={fieldErrors.sensitiveDataConsentAt}
                />
              )}

              {/* Opt-ins — read-only (managed elsewhere; default on at create). */}
              {isEditMode && record && (
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">
                    {t("contacts:form.fields.optIns.label", { defaultValue: "Opt-ins" })}
                  </Text>
                  <Group gap="lg">
                    <Switch
                      label={t("contacts:form.fields.optInForNotifications.label", { defaultValue: "Notifications" })}
                      checked={!!record.optInForNotifications}
                      readOnly
                      size="md"
                    />
                    <Switch
                      label={t("contacts:form.fields.optInForMessages.label", { defaultValue: "Messages" })}
                      checked={!!record.optInForMessages}
                      readOnly
                      size="md"
                    />
                    <Switch
                      label={t("contacts:form.fields.optInForReminders.label", { defaultValue: "Reminders" })}
                      checked={!!record.optInForReminders}
                      readOnly
                      size="md"
                    />
                  </Group>
                </Stack>
              )}
            </Stack>
          </Collapse>
        </Stack>
      )}
    </AppDrawer>
  );
};
